import { useMemo, useState } from "react";
import { Row, Col, Space, Card, Typography, Divider } from "antd";
import _ from "lodash";
import EngagementChartPanel from "./components/engagement.chart.panel";
import FilterToolbar from "./components/filter.toolbar";
import {
  useClickAnalyticsData,
  useEngagementChartData,
  useEngagementRadarData,
} from "../../hooks/analytics";
import {
  useCpdAnalyticsSummary,
  useCpdAnalyticsByDimension,
  useCpdTopPerformers,
} from "../../hooks/cpd";
import dayjs from "dayjs";
import ExportableRadarChartPanel from "./components/engagement.radar.chart.panel";
import CardSummaryPanel from "./components/summary.panel";
import functions from "../../utils/functions";
import EngagementTablePanel from "./components/engagement.table.panel";
import EngagementToolbar from "./components/engagement.toolbar";
import CpdSummaryPanel from "./components/summary.panel.cpd";
import CpdDimensionPanel from "./components/dimension.panel.cpd";
import CpdTopPerformersPanel from "./components/top.performers.panel.cpd";
import CpdUserProgressPanel from "./components/user.progress.panel.cpd";
import CpdFilterToolbar from "./components/filter.toolbar.cpd";

const { Title, Text } = Typography;
const { splitAndFirstLetterOnlyToUpperCase } = functions;

const AnalyticsPage = () => {
  const [filters, setFilters] = useState({
    dateRange: [dayjs().subtract(1, "month"), dayjs()],
    designations: [],
    conditions: [],
    designationRankingLimit: 5,
    conditionRankingLimit: 5,
  });

  const [cpdFilters, setCpdFilters] = useState({
    dateRange: [dayjs().subtract(1, "month"), dayjs()],
  });

  const [activeDimension, setActiveDimension] = useState("designation");
  const [displayMode, setDisplayMode] = useState("line");

  const { clickData = [] } = useClickAnalyticsData(filters);

  const previousClickDatePeriod = useMemo(() => {
    if (!filters.dateRange || filters.dateRange.length !== 2) {
      return [dayjs().subtract(2, "month"), dayjs().subtract(1, "month")];
    }
    
    const [fromDate, toDate] = filters.dateRange;
    const analyticsWindow = toDate.diff(fromDate);

    const previousPeriodDateRange = [
      fromDate.subtract(analyticsWindow),
      fromDate,
    ];

    return previousPeriodDateRange;
  }, [filters.dateRange]);

  const { clickData: previousPeriodClickData = [] } = useClickAnalyticsData({
    dateRange: previousClickDatePeriod,
  });

  const activelyEngagingUsersCount = useMemo(() => {
    return _.uniqBy(clickData, (elem) => elem?.user.username).length;
  }, [clickData]);

  const previouslyEngagingUsersCount = useMemo(() => {
    return _.uniqBy(previousPeriodClickData, (elem) => elem?.user.username)
      .length;
  }, [previousPeriodClickData]);

  const engagementUserCount = useMemo(() => {
    return {
      activeUsers: activelyEngagingUsersCount,
      change: activelyEngagingUsersCount - previouslyEngagingUsersCount,
    };
  }, [activelyEngagingUsersCount, previouslyEngagingUsersCount]);

  const guidelineViewsCount = useMemo(() => {
    return {
      guidelineViews: clickData.length,
      change: clickData.length - previousPeriodClickData.length,
    };
  }, [clickData, previousPeriodClickData]);

  const topVisitedConditions = useMemo(() => {
    const groupedByCondition = _.groupBy(clickData, "conditionName");

    return _.orderBy(
      _.map(groupedByCondition, (group, condition) => ({
        condition,
        totalClicks: group.length,
      })),
      "totalClicks",
      "desc"
    );
  }, [clickData]);

  const previousTopVisitedConditions = useMemo(() => {
    const groupedByCondition = _.groupBy(
      previousPeriodClickData,
      "conditionName"
    );

    return _.orderBy(
      _.map(groupedByCondition, (group, condition) => ({
        condition,
        totalClicks: group.length,
      })),
      "totalClicks",
      "desc"
    );
  }, [previousPeriodClickData]);

  const currentTopVisitedConditions = topVisitedConditions.map((elem) => {
    const conditionMatch =
      _.find(
        previousTopVisitedConditions,
        (prev) => prev.condition === elem.condition
      ) || {};

    return {
      ...elem,
      change: elem.totalClicks - conditionMatch?.totalClicks || 0,
    };
  });

  const topEngagingDesignations = useMemo(() => {
    const groupedByDesignation = _.groupBy(
      clickData,
      (elem) => elem.user.designation
    );

    return _.orderBy(
      _.map(groupedByDesignation, (group, designation) => ({
        designation,
        totalClicks: group.length,
      })),
      "totalClicks",
      "desc"
    );
  }, [clickData]);

  const previousTopEngagingDesignations = useMemo(() => {
    const groupedByDesignation = _.groupBy(
      previousPeriodClickData,
      (elem) => elem.user.designation
    );

    return _.orderBy(
      _.map(groupedByDesignation, (group, designation) => ({
        designation,
        totalClicks: group.length,
      })),
      "totalClicks",
      "desc"
    );
  }, [previousPeriodClickData]);

  const currentTopEngagingDesignations = topEngagingDesignations.map((elem) => {
    const designationMatch =
      _.find(
        previousTopEngagingDesignations,
        (prev) => prev.designation === elem.designation
      ) || {};

    return {
      ...elem,
      designation: splitAndFirstLetterOnlyToUpperCase(elem.designation, "_"),
      change: elem.totalClicks - designationMatch?.totalClicks || 0,
    };
  });

  const {
    chartData,
    loading,
    period,
    setPeriod,
    chartType,
    setChartType,
    isStacked,
    setIsStacked,
    groupBy,
    setGroupBy,
  } = useEngagementChartData({ clickData, filters });

  const { radarData } = useEngagementRadarData({ clickData, filters });

  // CPD Analytics data
  const { data: currentCpdSummary, isLoading: cpdSummaryLoading } = useCpdAnalyticsSummary(cpdFilters);
  const { data: cpdDimensionData = [], isLoading: cpdDimensionLoading } = useCpdAnalyticsByDimension(
    activeDimension,
    cpdFilters
  );
  const { data: cpdTopPerformers = [], isLoading: cpdTopPerformersLoading } = useCpdTopPerformers(cpdFilters);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card bordered={false}>
        <Title level={2} style={{ marginBottom: 8 }}>
          Analytics Dashboard
        </Title>
        <Text type="secondary">
          Comprehensive analytics for engagement and CPD activities
        </Text>
      </Card>

      {/* Engagement Analytics Section */}
      <Card
        title={<Title level={3} style={{ margin: 0 }}>Engagement Analytics</Title>}
        bordered={false}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <FilterToolbar filters={filters} onFilterChange={setFilters} />
      <CardSummaryPanel
        filters={filters}
        usersCount={engagementUserCount}
        guidelinesCount={guidelineViewsCount}
        conditionsData={currentTopVisitedConditions}
        designationData={currentTopEngagingDesignations}
      />

      {/* Single, shared toolbar (always visible) */}
      <EngagementToolbar
        chartType={chartType} setChartType={setChartType}
        isStacked={isStacked} setIsStacked={setIsStacked}
        displayMode={displayMode} setDisplayMode={setDisplayMode}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          {
            displayMode === "table" ? (
              <EngagementTablePanel
                  clickData={clickData}
                  selectedDesignations={filters.designations}
                  selectedConditions={filters.conditions}
                  dateRange={filters.dateRange}
                  onResetZoom={() => {}}
                  period={period}
                  setPeriod={setPeriod}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
              />
          ):
              (
              <EngagementChartPanel
                  loading={loading}
                  chartData={chartData}
                  period={period}
                  setPeriod={setPeriod}
                  chartType={chartType}
                  setChartType={setChartType}
                  isStacked={isStacked}
                  setIsStacked={setIsStacked}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
                  dateRange={filters.dateRange}
              />
          )}

        </Col>

        <Col xs={24} lg={10}>
          <ExportableRadarChartPanel
            data={radarData}
            dateRange={filters.dateRange}
          />
        </Col>
      </Row>
        </Space>
      </Card>

      <Divider style={{ margin: "8px 0" }} />

      {/* CPD Analytics Section */}
      <Card
        title={<Title level={3} style={{ margin: 0 }}>CPD Analytics</Title>}
        bordered={false}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <CpdFilterToolbar filters={cpdFilters} onFilterChange={setCpdFilters} />

          <CpdSummaryPanel
            summary={currentCpdSummary}
            loading={cpdSummaryLoading}
            dateRange={cpdFilters.dateRange}
          />

          <Row gutter={[24, 24]}>
            <Col xs={24} xl={16}>
              <CpdDimensionPanel
                dimensionData={cpdDimensionData}
                activeDimension={activeDimension}
                onDimensionChange={setActiveDimension}
                loading={cpdDimensionLoading}
                dateRange={cpdFilters.dateRange}
              />
            </Col>

            <Col xs={24} xl={8}>
              <CpdTopPerformersPanel
                performers={cpdTopPerformers}
                loading={cpdTopPerformersLoading}
              />
            </Col>
          </Row>

          <CpdUserProgressPanel filters={cpdFilters} />
        </Space>
      </Card>
    </Space>
  );
};

export default AnalyticsPage;
